import { RuleId, sigKey } from '../rules';
import { DeductionGraph, Support, buildGraph } from './graph';
import { ANALYZER_VERSION } from './version';

// Structural difficulty profile of a puzzle, derived from its deduction graph.
//
// This is the half of difficulty that needs no player model: how deep the reasoning goes, how
// narrow it gets, which techniques it cannot be solved without. The timing model layers on top
// and turns this into a predicted number of seconds.

/** All rules the enumerator can apply, easiest-looking first. */
export const ALL_RULES: RuleId[] = ['R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R8', 'R9'];

/**
 * How hard a technique is to *spot*, mirroring the hints panel's tiering: a forced placement is
 * the most obvious move on the board, the single-unit rules sit in the middle, and R9 — which
 * reasons about two units jointly — is the one people stall on. Used to pick the easiest way to
 * reach each fact, on the assumption a solver takes the simplest route available.
 */
export function ruleDifficulty(rule: RuleId): number {
  if (rule === 'R8') return 0;
  if (rule === 'R9') return 3;
  if (rule === 'R5' || rule === 'R6') return 2;
  return 1;
}

export interface RoundSummary {
  round: number;
  /** Facts first derivable at this round. */
  newFacts: number;
  /** Distinct supports that first become usable here. */
  supports: number;
  /** Difficulty of the easiest technique needed to make any progress at this round. */
  minRuleDifficulty: number;
  /** The rules that actually carry this round. */
  rules: RuleId[];
}

export interface Bottleneck {
  round: number;
  /** How many facts the round yields — the narrower, the more it is a single "aha". */
  width: number;
  /** Signature keys of the supports that carry it. */
  sigs: string[];
}

export interface PuzzleProfile {
  engineVersion: string;
  size: number;
  /** Did the rule closure finish the puzzle? false ⇒ needs reasoning the engine cannot express. */
  solved: boolean;
  /** Parallel closure depth — a lower bound on how many "waves" of deduction the puzzle needs. */
  depth: number;
  derivedFacts: number;
  supports: number;
  /** Rules without which the closure can no longer finish. */
  requiredRules: RuleId[];
  /** For rules that are not required: extra rounds needed when withheld (null ⇒ no effect). */
  costOfAvoiding: Partial<Record<RuleId, number | null>>;
  /** Hardest rule the puzzle genuinely requires — the headline "what makes this puzzle hard". */
  hardestRequiredRule: RuleId | null;
  perRound: RoundSummary[];
  bottlenecks: Bottleneck[];
  /** Facts reachable exactly one way — no alternative route, so a solver must find that one. */
  singleSupportFacts: number;
  /** sigKey → how many facts are most easily reached by that technique. */
  techniqueSpectrum: Record<string, number>;
}

/** The easiest support for a fact — the route a competent solver would most likely take. */
export function easiestSupport(g: DeductionGraph, supportIds: number[]): Support | null {
  let best: Support | null = null;
  for (const id of supportIds) {
    const s = g.supports[id];
    if (!best) { best = s; continue; }
    const d = ruleDifficulty(s.sig.rule) - ruleDifficulty(best.sig.rule);
    if (d < 0 || (d === 0 && s.round < best.round)) best = s;
  }
  return best;
}

/** Structural profile of an already-built graph (no ablation — see computeProfile). */
export function profileGraph(g: DeductionGraph): Omit<PuzzleProfile, 'requiredRules' | 'costOfAvoiding' | 'hardestRequiredRule'> {
  const byRound = new Map<number, { facts: number; supports: Set<number>; rules: Set<RuleId>; sigs: Set<string> }>();
  const spectrum: Record<string, number> = {};
  let derived = 0;
  let singleSupport = 0;

  for (const f of g.facts) {
    if (!f.derived) continue;
    derived++;
    if (f.bookkeeping) continue; // queen reach is not a technique
    if (f.supports.length === 1) singleSupport++;

    const best = easiestSupport(g, f.supports);
    if (!best) continue;
    const k = sigKey(best.sig);
    spectrum[k] = (spectrum[k] ?? 0) + 1;

    const slot = byRound.get(f.round) ?? byRound.set(f.round, { facts: 0, supports: new Set(), rules: new Set(), sigs: new Set() }).get(f.round)!;
    slot.facts++;
    slot.supports.add(best.id);
    slot.rules.add(best.sig.rule);
    slot.sigs.add(k);
  }

  const perRound: RoundSummary[] = [...byRound.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([round, s]) => ({
      round,
      newFacts: s.facts,
      supports: s.supports.size,
      minRuleDifficulty: Math.min(...[...s.rules].map(ruleDifficulty)),
      rules: [...s.rules].sort(),
    }));

  // A narrow round is where the puzzle actually resists: there is only one thing to see, and
  // until you see it nothing else moves.
  const bottlenecks: Bottleneck[] = [...byRound.entries()]
    .filter(([, s]) => s.facts <= 2)
    .sort((a, b) => a[0] - b[0])
    .map(([round, s]) => ({ round, width: s.facts, sigs: [...s.sigs].sort() }));

  return {
    engineVersion: g.engineVersion,
    size: g.size,
    solved: g.solved,
    depth: g.rounds,
    derivedFacts: derived,
    supports: g.supports.length,
    perRound,
    bottlenecks,
    singleSupportFacts: singleSupport,
    techniqueSpectrum: spectrum,
  };
}

/**
 * R8 (forced queen) is the only rule that ever *places* a queen, so the closure can never
 * finish without it and every solvable puzzle trivially "requires" it. Reporting that says
 * nothing about the puzzle. The meaningful question is which *elimination* techniques are
 * unavoidable, so callers should use `requiredTechniques()` for anything user-facing.
 */
export const TRIVIALLY_REQUIRED: RuleId = 'R8';

/** Required rules minus the ones every puzzle needs by construction. */
export function requiredTechniques(required: RuleId[]): RuleId[] {
  return required.filter(r => r !== TRIVIALLY_REQUIRED);
}

/**
 * Which techniques the puzzle cannot be solved without, by ablation: rebuild the closure with
 * each rule withheld and see whether it still finishes. This is the exact answer to "does this
 * puzzle train pigeonhole?", and it is what goal 3 (generate puzzles for a strategy) keys on.
 */
export function requiredRules(colorRegions: string[][]): {
  required: RuleId[];
  costOfAvoiding: Partial<Record<RuleId, number | null>>;
} {
  const base = buildGraph(colorRegions, { supportHorizon: 0 });
  const required: RuleId[] = [];
  const costOfAvoiding: Partial<Record<RuleId, number | null>> = {};

  for (const rule of ALL_RULES) {
    const g = buildGraph(colorRegions, { disable: new Set([rule]), supportHorizon: 0 });
    if (base.solved && !g.solved) {
      required.push(rule);
      costOfAvoiding[rule] = null; // cannot be avoided at all
    } else {
      costOfAvoiding[rule] = g.rounds - base.rounds;
    }
  }
  return { required, costOfAvoiding };
}

export function computeProfile(colorRegions: string[][]): PuzzleProfile {
  const g = buildGraph(colorRegions);
  const structural = profileGraph(g);
  const { required, costOfAvoiding } = requiredRules(colorRegions);
  // R8 excluded: every solvable puzzle requires it, so counting it as "the hardest thing this
  // puzzle needs" would label the easiest puzzles as needing a technique.
  const techniques = requiredTechniques(required);
  const hardest = techniques.length
    ? techniques.reduce((a, b) => (ruleDifficulty(b) > ruleDifficulty(a) ? b : a))
    : null;
  return {
    ...structural,
    engineVersion: ANALYZER_VERSION,
    requiredRules: required,
    costOfAvoiding,
    hardestRequiredRule: hardest,
  };
}
